import type { StateCreator } from 'zustand';
import type { Patch, TripParticipant } from '@my-fullstack-app/itinerary-types';
import { generateId } from '../helpers';
import { attributeOrphanCosts, pruneTripCosts } from '../../lib/cost-migration';
import type { ItineraryState } from '../itinerary-store';
import type { TripField } from '../../lib/sync-manager';
import { markTripDirty } from '../sync-bridge';

/**
 * CRUD for `Trip.participants[]` — phase 1 of the cost-tracking
 * feature (ADR-0057). Marks 'participants' dirty as its own wire
 * field, parallel to `journeys` and `stays`.
 *
 * `linkParticipantToUser` / `unlinkParticipantFromUser` toggle the
 * optional `userId` without touching `displayName` — linking is a
 * separate user action from creation, and the user's typed name
 * always wins over any name on the linked account.
 */
// Fields where an explicit `undefined` in an update patch means
// "delete the key" (vs. the applyPatch convention of "skip"). All
// optional fields per `TripParticipant`; required fields (id,
// displayName) deliberately omitted so a buggy caller can't
// corrupt a participant.
const CLEARABLE_FIELDS = new Set(['avatarUrl', 'userId']);

export interface ParticipantSlice {
  addParticipant: (
    tripId: string,
    participant: Omit<TripParticipant, 'id'> & { id?: string | undefined},
  ) => void;
  removeParticipant: (tripId: string, participantId: string) => void;
  updateParticipant: (
    tripId: string,
    participantId: string,
    patch: Patch<Omit<TripParticipant, 'id'>>,
  ) => void;
  /** Links a participant to a Kiips user. `avatarUrl` is the
   *  denormalised snapshot from the corresponding `TripShare` so
   *  the participant tile can render the user's photo without a
   *  live lookup.
   *
   *  Three-way semantics on the `avatarUrl` argument:
   *    undefined → preserve any existing snapshot (e.g. update
   *                just the userId)
   *    null      → explicit clear (e.g. relinking to a user whose
   *                share-enrichment has no photo)
   *    string    → set the snapshot (must pass `avatarUrlSchema`
   *                at the wire boundary)
   *
   *  Note: this diverges from `updateParticipant`'s patch convention
   *  where `undefined` means "clear this clearable field". The split
   *  is deliberate — link/unlink update userId atomically, and the
   *  avatarUrl semantics piggy-back on the same call; the patch
   *  convention is for partial in-place edits where a user clearing
   *  a field is the common case. */
  linkParticipantToUser: (
    tripId: string,
    participantId: string,
    userId: string,
    avatarUrl?: string | null | undefined,
  ) => void;
  unlinkParticipantFromUser: (tripId: string, participantId: string) => void;
}

export const createParticipantSlice: StateCreator<ItineraryState, [], [], ParticipantSlice> = (set, get) => ({
  addParticipant: (tripId, participantInput) => {
    get()._capture();
    const participant: TripParticipant = {
      ...participantInput,
      // `||`, not `??`: an empty-string id from a buggy caller would
      // otherwise pass through unchanged. That would collapse every
      // such participant onto palette[0] in the avatar gradient
      // (FallbackImage hashes seed='' → h=0 → first palette entry)
      // and clash with any other empty-id participant across the
      // app. Force a real id whenever the input is falsy.
      id: participantInput.id || generateId(),
    };
    // Backfill orphan costs (paidBy: undefined) onto the new
    // participant whenever this add is the 0 → 1 transition — either
    // the participants field has never been set, OR the user cleared
    // it down to []. In both cases there's no payer the orphans could
    // mean other than whoever joins next, so the newcomer is the
    // natural default. removeParticipant cascade-prunes paidBy refs
    // to the removed person, so a fresh-after-clear add isn't
    // inheriting anyone's preserved spend — the system already lost
    // the old attribution at removal time.
    const cascadeFields = new Set<TripField>();
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        const wasEmpty = (t.participants ?? []).length === 0;
        const withParticipant = {
          ...t,
          participants: [...(t.participants ?? []), participant],
        };
        if (!wasEmpty) return withParticipant;
        const after = attributeOrphanCosts(withParticipant, participant.id);
        // Mark only the collections the backfill actually touched.
        // mergeServerTrip is whole-field local-wins on hydrate, so an
        // over-broad mark would clobber a co-editor's concurrent
        // journey / stay additions when only place costs changed.
        if (after.days !== withParticipant.days) cascadeFields.add('days');
        if (after.journeys !== withParticipant.journeys) cascadeFields.add('journeys');
        if (after.stays !== withParticipant.stays) cascadeFields.add('stays');
        return after;
      }),
    }));
    const fields: TripField[] = ['participants', ...cascadeFields];
    markTripDirty(tripId, fields);
  },

  removeParticipant: (tripId, participantId) => {
    get()._capture();
    // Cascade-prune ADR-0069's inline cost line items so any item
    // that referenced the removed participant has paidBy / splitWith
    // cleaned immediately (read-time tolerance + hydrate prune would
    // handle it, but the local cleanup keeps the UI honest without a
    // round trip). Walks Place / CustomEntry / Booking inline costs.
    let cascadedCosts = false as boolean;
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        const next = {
          ...t,
          participants: (t.participants ?? []).filter((p) => p.id !== participantId),
        };
        const after = pruneTripCosts(next);
        cascadedCosts = after !== next;
        return after;
      }),
    }));
    const fields: TripField[] = ['participants'];
    if (cascadedCosts) {
      // Line items live on place / custom (days) and on
      // booking (journeys / stays). Mark everything that
      // could have changed; sync-manager dedupes.
      fields.push('days', 'journeys', 'stays');
    }
    markTripDirty(tripId, fields);
  },

  updateParticipant: (tripId, participantId, patch) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          participants: (t.participants ?? []).map((p) => {
            if (p.id !== participantId) return p;
            // Explicit `undefined` is a CLEAR intent for OPTIONAL
            // fields (e.g. user emptied the nickname). Required
            // fields fall through to the applyPatch convention —
            // undefined is skipped to keep the participant valid.
            // Missing keys are always left untouched.
            const next: Record<string, unknown> = { ...p };
            for (const [k, v] of Object.entries(patch)) {
              if (v === undefined) {
                if (CLEARABLE_FIELDS.has(k)) delete next[k];
                // else: ignore — refusing to corrupt required fields.
              } else {
                next[k] = v;
              }
            }
            return next as unknown as TripParticipant;
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['participants']);
  },

  linkParticipantToUser: (tripId, participantId, userId, avatarUrl) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          participants: (t.participants ?? []).map((p) => {
            if (p.id !== participantId) return p;
            const next: TripParticipant = { ...p, userId };
            // Three-way semantics, explicit so a stray empty string
            // can't masquerade as a clear:
            //   undefined → leave any existing avatarUrl alone
            //   null      → drop the snapshot (linked user has no avatar)
            //   string    → write through (must be non-empty per the
            //               participantSchema's URL validation)
            if (avatarUrl === null) {
              delete next.avatarUrl;
            } else if (typeof avatarUrl === 'string' && avatarUrl.length > 0) {
              next.avatarUrl = avatarUrl;
            }
            return next;
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['participants']);
  },

  unlinkParticipantFromUser: (tripId, participantId) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          participants: (t.participants ?? []).map((p) => {
            if (p.id !== participantId) return p;
            // Drop userId and the denormalised avatarUrl snapshot —
            // the avatar belonged to the linked account, so leaving
            // it behind would render a stale photo for an unlinked
            // participant. Preserve names so the user doesn't lose
            // typing customisation on an unlink.
            const { userId: _drop, avatarUrl: _drop2, ...rest } = p;
            return rest;
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['participants']);
  },
});
